import React from "react";
import { useField, useFormikContext } from "formik";
import { Typeahead, TypeaheadComponentProps } from "react-bootstrap-typeahead";
import "react-bootstrap-typeahead/css/Typeahead.css";
import { IControlStyles, useControlStyles } from "../.";
import "./autocomplete.css";

export interface AutocompleteOption {
    id: string;
    name: string;
}

export interface ControlAutocompleteProps
    extends Omit<TypeaheadComponentProps, "options"> {
    options: AutocompleteOption[];
    controlStyles?: IControlStyles;
    isTouched?: boolean;
    name: string;
    multiple?: boolean;
    filterType?: "starts-with" | "contains" | "ends-with" | undefined
}

export const ComponentProps = (
    props: ControlAutocompleteProps
): TypeaheadComponentProps => {
    const componentProps = Object.assign({}, props);
    delete componentProps.controlStyles;
    delete componentProps.isTouched;
    return componentProps;
};

export const ControlAutocomplete: React.FC<ControlAutocompleteProps> = (props) => {
    const styles: IControlStyles = props.controlStyles ?? useControlStyles();
    const [field, meta] = useField(props.name ?? "");
    const { setFieldValue, setFieldTouched } = useFormikContext();
    const { value } = field;
    const isMultiple = props.multiple ?? false;
    const className: string = (props.className ? props.className + " " : "") + "autocomplete " + (meta.touched && meta.error ? "is-invalid" : "");

    const selectedItems = React.useMemo(() => {
        if (!props.options) return [];
        if (isMultiple) {
            if (!Array.isArray(value)) {
                return [];
            }

            return props.options.filter((opt) => value.includes(opt.id));
        } else {
            return props.options.filter((opt) => opt.id === value);
        }
    }, [value, props.options, isMultiple]);

    const customFilter = (option: any, filterProps: any) => {
        const searchText = filterProps.text.toLowerCase();
        const optionName = option.name.toLowerCase();

        switch (props.filterType) {
            case "starts-with":
                return optionName.startsWith(searchText);
                break;
            case "contains":
                return optionName.contains(searchText);
                break;
            case "ends-with":
                return optionName.endsWith(searchText);
                break;
            default:
                return optionName.startsWith(searchText);
                break;
        }


    };

    return (
        <>
        <Typeahead
            {...ComponentProps(props)}
            className={className}
            id={props.id ?? props.name}
            multiple={isMultiple}
            selected={selectedItems}
            placeholder={props.placeholder ?? ""}
            labelKey="name"
            options={props.options}
            filterBy={customFilter}
            ignoreDiacritics={true}
            style={{
                ...(styles.autocomplete ?? styles.text ?? styles.input),
                ...props.style,
            }}
            isInvalid={meta?.touched && !!meta?.error}
            onChange={(selected) => {
                if (isMultiple) {
                    const selectedIds = (selected as AutocompleteOption[]).map(
                        (s) => s.id
                    );
                    setFieldValue(props.name, selectedIds);
                } else {
                    const selectedItem = selected[0] as AutocompleteOption;
                    const selectedId = selectedItem?.id ?? null;
                    setFieldValue(props.name, selectedId);
                }
            }}
            onBlur={() => setFieldTouched(props.name, true)}
            renderMenuItemChildren={(option, _menuProps, index) => {
                if (typeof option === "string") {
                    return <div key={option}>{option}</div>;
                }
                return <div key={option.id}>{option.name}</div>;
            }}
        />
        {meta?.error &&  <div className="invalid-feedback">{meta?.error}</div>}
        </>
    );
};
